import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Trash2, Eye } from "lucide-react";
import { CampaignDialog, type SocialCampaign } from "./CampaignDialog";
import { CampaignDetail } from "./CampaignDetail";
import {
  PLATFORMS, CAMPAIGN_STATUSES, CAMPAIGN_TYPES, getPlatformColor
} from "./constants";

export function CampaignsPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<SocialCampaign | null>(null);
  const [drillIntoId, setDrillIntoId] = useState<string | null>(null);

  if (drillIntoId) {
    return <CampaignDetail campaignId={drillIntoId} onBack={() => setDrillIntoId(null)} />;
  }

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['social_campaigns', filterStatus, filterType],
    queryFn: async () => {
      let q = supabase
        .from('social_campaigns' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') q = q.eq('status', filterStatus);
      if (filterType !== 'all') q = q.eq('campaign_type', filterType);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SocialCampaign[];
    },
  });

  // Fetch task counts grouped by campaign for progress display
  const { data: taskStats = {} } = useQuery({
    queryKey: ['campaign_task_stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('campaign_id, completed')
        .not('campaign_id', 'is', null);
      if (error) throw error;
      const stats: Record<string, { total: number; done: number }> = {};
      (data ?? []).forEach((t: any) => {
        if (!t.campaign_id) return;
        const s = stats[t.campaign_id] ?? { total: 0, done: 0 };
        s.total += 1;
        if (t.completed) s.done += 1;
        stats[t.campaign_id] = s;
      });
      return stats;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (campaign: SocialCampaign) => {
      if (campaign.id) {
        const { id, ...updates } = campaign;
        const { error } = await supabase
          .from('social_campaigns' as any)
          .update(updates)
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('social_campaigns' as any)
          .insert([campaign]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_campaigns'] });
      setDialogOpen(false);
      setEditingCampaign(null);
      toast({ title: 'Campaign saved' });
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('social_campaigns' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_campaigns'] });
      toast({ title: 'Campaign deleted' });
    },
    onError: (e: Error) => toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }),
  });

  const getStatusBadge = (status: string) => {
    const s = CAMPAIGN_STATUSES.find(x => x.id === status);
    return s
      ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
      : <span className="text-muted-foreground text-xs">{status}</span>;
  };

  const renderPlatformDots = (platforms: string[]) => (
    <div className="flex flex-wrap gap-1">
      {platforms.map(p => {
        const color = getPlatformColor(p);
        const displayColor = color === '#000000' ? '#374151' : color;
        const label = PLATFORMS.find(x => x.id === p)?.label ?? p;
        return (
          <span
            key={p}
            title={label}
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: displayColor }}
          />
        );
      })}
    </div>
  );

  const renderGeoScope = (scope: string[]) => {
    if (!scope || scope.length === 0) return <span className="text-muted-foreground">—</span>;
    if (scope.includes('ALL')) return <span className="text-xs font-medium">National</span>;
    if (scope.length <= 4) return <span className="text-xs">{scope.join(', ')}</span>;
    return <span className="text-xs">{scope.slice(0, 3).join(', ')} +{scope.length - 3}</span>;
  };

  const getCampaignTypeLabel = (type: string | null) => {
    if (!type) return '—';
    return CAMPAIGN_TYPES.find(t => t.id === type)?.label ?? type;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Campaigns</CardTitle>
              <CardDescription>
                {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button onClick={() => { setEditingCampaign(null); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {CAMPAIGN_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-44"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CAMPAIGN_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Posts</TableHead>
                  <TableHead>Tasks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">Loading campaigns...</TableCell>
                  </TableRow>
                ) : campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No campaigns yet. Create your first campaign!
                    </TableCell>
                  </TableRow>
                ) : campaigns.map((camp: any) => (
                  <TableRow key={camp.id}>
                    <TableCell>
                      <button
                        onClick={() => setDrillIntoId(camp.id)}
                        className="text-left font-medium text-sm hover:underline hover:text-primary"
                      >
                        {camp.name}
                      </button>
                      {camp.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {camp.description.slice(0, 60)}{camp.description.length > 60 ? '…' : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{getCampaignTypeLabel(camp.campaign_type)}</TableCell>
                    <TableCell>{renderPlatformDots(camp.platforms ?? [])}</TableCell>
                    <TableCell>{renderGeoScope(camp.geo_scope ?? [])}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {camp.start_date
                        ? `${new Date(camp.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} → ${camp.end_date ? new Date(camp.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '…'}`
                        : '—'
                      }
                    </TableCell>
                    <TableCell>{getStatusBadge(camp.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{camp.post_count ?? 0}</TableCell>
                    <TableCell className="text-xs">
                      {(() => {
                        const s = taskStats[camp.id];
                        if (!s || s.total === 0) return <span className="text-muted-foreground">—</span>;
                        const pct = Math.round((s.done / s.total) * 100);
                        const color = pct === 100 ? 'text-green-600' : pct >= 50 ? 'text-blue-600' : 'text-gray-700';
                        return (
                          <span className={`font-medium ${color}`}>
                            {s.done}/{s.total} ({pct}%)
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm" variant="ghost"
                          title="View campaign detail"
                          onClick={() => setDrillIntoId(camp.id)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { setEditingCampaign(camp); setDialogOpen(true); }}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { if (confirm('Delete this campaign?')) deleteMutation.mutate(camp.id); }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CampaignDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        campaign={editingCampaign}
        onSave={data => saveMutation.mutate(data)}
        isPending={saveMutation.isPending}
      />
    </div>
  );
}
